import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, useApiResource } from '../../lib/api/queries';
import type {
  BoothSlot,
  BoothTemplate,
  BrandingAsset,
  CurrentHall,
  Expo,
  ExpoLayoutConfig,
  ExpoLayoutSnapshot,
  ExpoLayoutState,
  ExpoLayoutValidation,
  HallLayout,
  Organization,
  Participation,
  Readiness,
  RevenueItem,
} from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select, TextArea } from '../../ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '../../ui/states';
import { ApprovalHandoff, AuditStatement, RefusalWithRemedy, StepUpPanel } from '../../ui/guards';
import { ReadinessPanel } from '../../ui/readiness';
import { MediaPreview, adminMediaPreviewUrl } from '../../ui/media';
import { useSession } from '../../lib/auth/session';
import type { HandoffGrant, HandoffRequest } from '../../lib/auth/handoff';
import { statusTone } from './Expos';
import type { StringKey } from '../../lib/i18n/strings';

const REASON_CODES = ['operational', 'commercial', 'compliance', 'security', 'administrative_error'];

const CATALOG_STATUS_KEYS: Record<string, StringKey> = {
  active: 'status.active',
  retired: 'status.retired',
  draft: 'status.draft',
  published: 'status.published',
};

/** The permission a booth take-down is gated on. */
const TAKE_DOWN = 'booth.take_down';

/**
 * THE WHOLE SOLVER CONFIGURATION, LABELLED — NOT FIVE OF ITS TWELVE FIELDS.
 *
 * `POST .../layout/generate` takes an `ExpoLayoutConfig`, and the seven fields this form used to
 * omit were not absent from the plan: the server filled them with ITS defaults, so an operator who
 * had changed one on a previous generation silently got it back. A configuration the operator
 * cannot see is a configuration they cannot reproduce, and the plan is meant to be deterministic
 * from exactly these numbers.
 */
const LAYOUT_CONFIG_FIELDS: { key: keyof ExpoLayoutConfig; label: StringKey; initial: string }[] = [
  { key: 'minimum_hall_width_m', label: 'admin.layoutEngine.minWidth', initial: '50' },
  { key: 'minimum_hall_depth_m', label: 'admin.layoutEngine.minDepth', initial: '48' },
  { key: 'ceiling_height_m', label: 'admin.layoutEngine.ceiling', initial: '11.6' },
  { key: 'main_aisle_width_m', label: 'admin.layoutEngine.aisle', initial: '12' },
  { key: 'cross_aisle_width_m', label: 'admin.layoutEngine.crossAisle', initial: '4.5' },
  { key: 'perimeter_margin_m', label: 'admin.layoutEngine.margin', initial: '2.5' },
  { key: 'entrance_depth_m', label: 'admin.layoutEngine.entrance', initial: '12' },
  { key: 'end_feature_depth_m', label: 'admin.layoutEngine.endFeature', initial: '12' },
  { key: 'bay_depth_m', label: 'admin.layoutEngine.bay', initial: '15' },
  { key: 'target_avenue_run_m', label: 'admin.layoutEngine.run', initial: '90' },
  { key: 'target_aspect_ratio', label: 'admin.layoutEngine.aspect', initial: '1.7' },
  { key: 'maximum_avenues', label: 'admin.layoutEngine.avenues', initial: '4' },
];

function initialLayoutConfig(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of LAYOUT_CONFIG_FIELDS) out[field.key] = field.initial;
  return out;
}

/** `ADM-06` — the commercial floor. */
export function AdminFloor(): ReactElement {
  const { expoId = '' } = useParams();
  const { t } = useI18n();
  const expoPath = `/v1/admin/expos/${expoId}`;
  const layoutPath = `${expoPath}/layout`;
  const slotPath = `${expoPath}/booth-slots`;
  const expo = useApiResource<Expo>(expoPath);
  const state = useApiResource<ExpoLayoutState & { version?: number }>(layoutPath);
  const slots = useApiList<BoothSlot>(slotPath);
  const generate = useApiAction<HallLayout>([layoutPath, '/v1/admin/hall-layouts']);
  const publish = useApiAction<ExpoLayoutState>([layoutPath, expoPath, slotPath]);
  const [config, setConfig] = useState<Record<string, string>>(initialLayoutConfig);
  const layoutState = state.data?.data;
  const expoData = expo.data?.data;
  const activeLayout = layoutState?.latest_draft ?? layoutState?.published;
  const snapshot = activeLayout?.resolved_snapshot?.hall ? activeLayout.resolved_snapshot : null;
  const validation: ExpoLayoutValidation | null =
    activeLayout?.validation_results ?? activeLayout?.resolved_snapshot?.validation ?? null;

  return (
    <>
      <PageTitle title={t('admin.slots.title')}>{t('admin.slots.desc')}</PageTitle>

      <CurrentHallCard
        hall={layoutState?.current_hall ?? null}
        loaded={Boolean(layoutState)}
        loading={state.isLoading}
      />

      <div className="expo-layout-workspace">
        <Card title={t('admin.layoutEngine.title')} description={t('admin.layoutEngine.desc')}>
          {state.isLoading || expo.isLoading ? <LoadingState /> : null}
          {state.error ? <ErrorState error={state.error} onRetry={() => void state.refetch()} /> : null}
          {expo.error ? <ErrorState error={expo.error} onRetry={() => void expo.refetch()} /> : null}
          {layoutState ? (
            <div className="expo-layout-metrics" aria-label={t('admin.layoutEngine.counts')}>
              <div><span>{t('admin.layoutEngine.expected')}</span><strong>{layoutState.expected_booth_count}</strong></div>
              <div><span>{t('admin.layoutEngine.actual')}</span><strong>{layoutState.actual_booth_count}</strong></div>
              <div><span>{t('admin.layoutEngine.status')}</span><strong>{activeLayout?.status ?? t('table.none')}</strong></div>
              <div><span>{t('admin.layoutEngine.version')}</span><strong>{activeLayout?.version ?? '—'}</strong></div>
            </div>
          ) : null}
          {layoutState?.blocking_issues.map((issue) => <p className="state state--error" role="alert" key={issue}>{issue}</p>)}
          {layoutState?.draft_stale ? <p className="state state--warning" role="status">{t('admin.layoutEngine.stale')}</p> : null}

          {/* THE SNAPSHOT'S OWN VERDICT, SHOWN. A plan the solver marked invalid, or warned about,
              used to be publishable from this screen with nothing on it saying so. */}
          {validation ? (
            <section className="layout-validation" aria-label={t('admin.layoutEngine.validation')}>
              <h3>{t('admin.layoutEngine.validation')}</h3>
              {validation.errors.map((issue) => (
                <p className="state state--error" role="alert" key={`e-${issue}`}>{issue}</p>
              ))}
              {validation.warnings.map((issue) => (
                <p className="state state--warning" role="status" key={`w-${issue}`}>{issue}</p>
              ))}
              {validation.errors.length === 0 && validation.warnings.length === 0 ? (
                <p className="muted">{t('admin.layoutEngine.validationClean')}</p>
              ) : null}
            </section>
          ) : null}

          <form
            className="expo-layout-controls"
            onSubmit={(ev) => {
              ev.preventDefault();
              const body: Record<string, number> = {};
              for (const field of LAYOUT_CONFIG_FIELDS) {
                body[field.key] = Number(config[field.key] ?? field.initial);
              }
              generate.mutate(newIntent({ path: `${layoutPath}/generate`, body: { config: body } }));
            }}
          >
            {LAYOUT_CONFIG_FIELDS.map((field) => (
              <Field
                key={field.key}
                label={t(field.label)}
                name={field.key}
                type="number"
                value={config[field.key] ?? field.initial}
                onChange={(next) => setConfig((current) => ({ ...current, [field.key]: next }))}
                ltr
                required
              />
            ))}
            <div className="row expo-layout-controls__actions">
              <button className="btn" type="submit" disabled={generate.isPending || Boolean(layoutState?.blocking_issues.length)}>{t('admin.layoutEngine.generate')}</button>
              <button className="btn btn--secondary" type="button"
                disabled={publish.isPending || !layoutState?.latest_draft || layoutState.draft_stale || !expoData}
                onClick={() => {
                  const draft = layoutState?.latest_draft;
                  if (!draft || !expoData) return;
                  publish.mutate(newIntent({ path: `${layoutPath}/publish`, ifMatch: expoData.version,
                    body: { layout_id: draft.hall_layout_id, layout_row_version: draft.row_version } }));
                }}>{t('admin.layoutEngine.publish')}</button>
            </div>
          </form>
          {generate.error ? <ErrorState error={generate.error} /> : null}
          {publish.error ? <ErrorState error={publish.error} /> : null}
        </Card>

        <Card title={t('admin.layoutEngine.preview')} description={t('admin.layoutEngine.previewHint')}>
          {snapshot ? <LayoutPlanPreview snapshot={snapshot} /> : <EmptyState>{t('admin.layoutEngine.empty')}</EmptyState>}
          {snapshot ? <ul className="expo-layout-explanation">{snapshot.explanation.map((line) => <li key={line}>{line}</li>)}</ul> : null}
        </Card>
      </div>

      <Card title={t('admin.layoutEngine.publishedSlots')}>
        {slots.isLoading ? <LoadingState /> : null}
        {slots.error ? <ErrorState error={slots.error} onRetry={() => void slots.refetch()} /> : null}
        {slots.data ? <DataTable<BoothSlot> caption={t('admin.layoutEngine.publishedSlots')}
          rows={slots.data.items ?? []} rowKey={(s) => s.booth_slot_id} empty={<EmptyState />}
          columns={[
            { key: 'code', header: t('table.code'), cell: (s) => <Ltr>{s.slot_code}</Ltr> },
            { key: 'tier', header: t('admin.layoutEngine.tier'), cell: (s) => s.plot_tier },
            { key: 'status', header: t('table.status'), cell: (s) => <Badge tone={statusTone(s.status)}>{s.status}</Badge> },
          ]} /> : null}
      </Card>
    </>
  );
}

/**
 * WHICH HALL IS THIS EXPO ACTUALLY RUNNING?
 *
 * There is one hall — `scenes/procedural-expo` — and the identity that matters at runtime is the
 * triple `(scene_key, scene_version, layout_version)`: Unity refuses anything else before it loads
 * a byte. `active` is the separate question of whether the expo POINTS at that published layout;
 * a layout can be published and the expo still attached to nothing, and that combination is the
 * one an operator most needs to be told about rather than left to infer from a blank world.
 */
function CurrentHallCard({
  hall,
  loaded,
  loading,
}: {
  hall: CurrentHall | null;
  loaded: boolean;
  loading: boolean;
}): ReactElement {
  const { t, formatDateTime } = useI18n();
  return (
    <Card title={t('admin.currentHall.title')} description={t('admin.currentHall.desc')} testId="current-hall">
      {loading ? <LoadingState rows={2} /> : null}
      {hall ? (
        <>
          <p className="row">
            <Badge tone={hall.active ? 'ok' : 'warn'}>
              {hall.active ? t('admin.currentHall.active') : t('admin.currentHall.inactive')}
            </Badge>
          </p>
          <dl className="kv">
            <dt>{t('admin.currentHall.sceneKey')}</dt>
            <dd><Ltr>{hall.scene_key}</Ltr></dd>
            <dt>{t('admin.currentHall.sceneVersion')}</dt>
            <dd className="numeral">{hall.scene_version}</dd>
            <dt>{t('admin.currentHall.hallId')}</dt>
            <dd><Ltr>{hall.hall_id}</Ltr></dd>
            <dt>{t('admin.currentHall.layoutVersion')}</dt>
            <dd className="numeral">{hall.layout_version}</dd>
            <dt>{t('admin.layouts.publishedAt')}</dt>
            <dd>{formatDateTime(hall.published_at)}</dd>
          </dl>
          {hall.active ? null : <p className="state state--warning" role="status">{t('admin.currentHall.inactiveHint')}</p>}
        </>
      ) : loaded ? (
        <EmptyState title={t('admin.currentHall.none')}>{t('admin.currentHall.noneHint')}</EmptyState>
      ) : null}
    </Card>
  );
}

function LayoutPlanPreview({ snapshot }: { snapshot: ExpoLayoutSnapshot }): ReactElement {
  const { t } = useI18n();
  const { width_m: width, depth_m: depth } = snapshot.hall;
  return <figure className="expo-layout-preview">
    <svg viewBox={`${-width / 2 - 2} ${-depth / 2 - 2} ${width + 4} ${depth + 4}`}
      role="img" aria-label={t('admin.layoutEngine.planAlt')} preserveAspectRatio="xMidYMid meet">
      <rect className="expo-layout-preview__hall" x={-width / 2} y={-depth / 2} width={width} height={depth} rx="1" />
      {snapshot.aisles.map((a) => <rect key={a.aisle_id} className={`expo-layout-preview__aisle expo-layout-preview__aisle--${a.kind}`}
        x={a.bounds.center.x - a.bounds.width_m / 2} y={a.bounds.center.z - a.bounds.depth_m / 2}
        width={a.bounds.width_m} height={a.bounds.depth_m}><title>{a.aisle_id}</title></rect>)}
      {snapshot.placements.map((p) => {
        const turned = Math.abs(p.yaw_degrees % 180) > 1;
        const w = turned ? p.depth_m : p.width_m;
        const d = turned ? p.width_m : p.depth_m;
        return <rect key={p.booth_instance_id}
          className={`expo-layout-preview__booth${p.flagship ? ' expo-layout-preview__booth--flagship' : ''}`}
          x={p.position.x - w / 2} y={p.position.z - d / 2} width={w} height={d} rx=".5">
          <title>{`${p.slot_code} · ${p.design_code}`}</title>
        </rect>;
      })}
      {snapshot.anchors.filter((a) => a.kind === 'entrance').map((a) => <circle key={a.anchor_id}
        className="expo-layout-preview__entrance" cx={a.position.x} cy={a.position.z} r="1.5"><title>{a.anchor_id}</title></circle>)}
    </svg>
    <figcaption>{snapshot.metrics.actual_booth_count} · {snapshot.hall.width_m}m × {snapshot.hall.depth_m}m · {snapshot.hall.avenue_count} {t('admin.layoutEngine.avenueUnit')}</figcaption>
  </figure>;
}

/** `ADM-07` participations and entitlements · `ADM-08` branding approval · `ADM-09` publication. */
export function AdminParticipations(): ReactElement {
  const { expoId = '' } = useParams();
  const { t } = useI18n();
  const path = `/v1/admin/expos/${expoId}/participations`;
  const list = useApiList<Participation>(path);
  const create = useApiAction<Participation>([path]);
  const templates = useApiList<BoothTemplate>('/v1/admin/booth-templates');
  const organizations = useApiList<Organization>('/v1/admin/organizations');
  const [selected, setSelected] = useState<string | null>(null);

  const [orgId, setOrgId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [seatCount, setSeatCount] = useState('1');
  const [commercialTier, setCommercialTier] = useState('standard');
  const templateItems = templates.data?.items ?? [];
  const organizationItems = (organizations.data?.items ?? []).filter((o) => o.type === 'developer');
  const orgName = (id: string): string => organizationItems.find((o) => o.organization_id === id)?.display_name ?? id;

  return (
    <>
      <PageTitle title={t('admin.expo.participations')}>{t('admin.participations.desc')}</PageTitle>

      <Card title={t('admin.expo.participations')}>
        {list.isLoading ? <LoadingState /> : null}
        {list.error ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
        {list.data ? (
          <DataTable<Participation>
            caption={t('admin.expo.participations')}
            rows={list.data.items ?? []}
            rowKey={(p) => p.participation_id}
            empty={<EmptyState>{t('admin.participations.desc')}</EmptyState>}
            columns={[
              {
                key: 'id',
                header: t('table.identifier'),
                cell: (p) => (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => setSelected(p.participation_id)}
                  >
                    <Ltr>{p.participation_id.slice(0, 8)}</Ltr>
                  </button>
                ),
              },
              { key: 'org', header: t('admin.participations.developer'), cell: (p) => orgName(p.developer_org_id) },
              {
                key: 'design',
                header: t('admin.participations.design'),
                cell: (p) => (p.booth_template_code ? <Ltr>{p.booth_template_code}</Ltr> : t('table.none')),
              },
              {
                key: 'slot',
                header: t('admin.participations.slot'),
                cell: (p) => (p.booth_slot_code ? <Ltr>{p.booth_slot_code}</Ltr> : t('table.none')),
              },
              {
                key: 'status',
                header: t('table.status'),
                cell: (p) => <Badge tone={statusTone(p.status)}>{p.status}</Badge>,
              },
              {
                key: 'publication',
                header: 'publication_state',
                cell: (p) => <Badge tone={statusTone(p.publication_state)}>{p.publication_state}</Badge>,
              },
              { key: 'stage', header: 'stage', cell: (p) => <Ltr>{p.stage}</Ltr> },
              { key: 'seats', header: 'seat_count', cell: (p) => <span className="numeral">{p.seat_count}</span> },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('action.create')}>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            const body: Record<string, unknown> = {
              developer_org_id: orgId,
              seat_count: Number(seatCount),
              commercial_tier: commercialTier,
            };
            if (templateId) body['booth_template_id'] = templateId;
            create.mutate(newIntent({ path, body }));
          }}
        >
          <Select label={t('admin.participations.developer')} name="developer_org_id" value={orgId}
            onChange={setOrgId} required options={[{ value: '', label: t('admin.participations.chooseDeveloper') },
              ...organizationItems.map((o) => ({ value: o.organization_id, label: o.display_name }))]} />
          <Select label={t('admin.participations.design')} name="booth_template_id" value={templateId}
            onChange={setTemplateId} required options={[{ value: '', label: t('admin.participations.chooseDesign') },
              ...templateItems.map((b) => ({ value: b.booth_template_id,
                label: `${b.display_name} · ${b.width_m}×${b.depth_m}m · ${b.max_seats} ${t('admin.templates.seatUnit')}` }))]} />
          <Select label={t('admin.participations.tier')} name="commercial_tier" value={commercialTier}
            onChange={setCommercialTier} options={['standard', 'premium', 'signature', 'flagship'].map((value) => ({ value, label: t(`admin.tier.${value}` as StringKey) }))} />
          <Field label={t('admin.participations.seats')} name="seat_count" type="number" value={seatCount} onChange={setSeatCount} ltr />
          {create.error ? <ErrorState error={create.error} /> : null}
          <button className="btn" type="submit" disabled={create.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>

      {selected ? <ParticipationDetail participationId={selected} expoId={expoId} /> : null}
    </>
  );
}

/**
 * The publication verbs, in the order an operator meets them. `take_down` is not here: it is a
 * terminal act with its own step-up, four-eyes and typed confirmation, and it keeps its own control.
 */
const PUBLICATION_VERBS = ['accept', 'withdraw', 'publish', 'republish', 'suspend'] as const;

const VERB_KEYS: Record<string, StringKey> = {
  accept: 'admin.verb.accept',
  withdraw: 'admin.verb.withdraw',
  publish: 'admin.verb.publish',
  republish: 'admin.verb.republish',
  suspend: 'admin.verb.suspend',
  take_down: 'admin.verb.take_down',
};

function ParticipationDetail({
  participationId,
  expoId,
}: {
  participationId: string;
  expoId: string;
}): ReactElement {
  const { t, formatDateTime } = useI18n();
  const resourcePath = `/v1/admin/participations/${participationId}`;
  const readinessPath = `${resourcePath}/readiness`;
  const listPath = `/v1/admin/expos/${expoId}/participations`;
  const participation = useApiResource<Participation>(resourcePath);
  // Readiness is derived state and carries no row version of its own; the read is still a resource
  // read so the query key matches the paths the mutations invalidate.
  const readiness = useApiResource<Readiness & { version?: number }>(readinessPath);
  const branding = useApiList<BrandingAsset>(`${resourcePath}/branding`);
  const revenue = useApiList<RevenueItem>(`${resourcePath}/revenue-items`);
  const designs = useApiList<BoothTemplate>('/v1/admin/booth-templates');
  const action = useApiAction<Participation>([resourcePath, readinessPath, listPath]);
  const updateDesign = useApiAction<Participation>([resourcePath, readinessPath, listPath, `/v1/admin/expos/${expoId}/layout`]);
  const review = useApiAction<BrandingAsset>([`${resourcePath}/branding`, readinessPath]);
  const addRevenue = useApiAction<RevenueItem>([`${resourcePath}/revenue-items`]);

  const session = useSession();
  const [reason, setReason] = useState(REASON_CODES[0] ?? 'operational');
  const [legalRef, setLegalRef] = useState('');
  const [typed, setTyped] = useState('');
  const [suspendTyped, setSuspendTyped] = useState('');
  const [kind, setKind] = useState('website_sponsorship');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);
  const [designId, setDesignId] = useState('');
  const [commercialTier, setCommercialTier] = useState('');
  const [brandingReasons, setBrandingReasons] = useState<Record<string, string>>({});

  const version = participation.data?.version ?? participation.data?.data.version ?? null;
  const readinessData = readiness.data?.data ?? null;
  const allowedActions = readinessData?.allowed_actions ?? [];

  /**
   * A VERB IS OFFERED BECAUSE THE SERVER SAID IT IS AVAILABLE.
   *
   * These five buttons used to be five always-enabled raw strings, so a `publish` on a booth with
   * no approved logo, or a `republish` from a state that has no such transition, was a refusal the
   * operator had to earn before learning it. `allowed_actions` is computed by the same code that
   * performs the transition, and `publishable` is the same conjunction the publish guard evaluates;
   * until readiness has been read this screen offers no verb at all, because it does not yet know
   * which ones exist.
   */
  const verbDisabled = (verb: string): boolean => {
    if (action.isPending || version === null || !readinessData) return true;
    if (!allowedActions.includes(verb)) return true;
    if (verb === 'publish' && !readinessData.publishable) return true;
    if (verb === 'suspend' && suspendTyped.trim() !== participationId) return true;
    return false;
  };

  /**
   * A BOOTH TAKE-DOWN IS STEP-UP **AND** FOUR EYES, and this screen offers both.
   *
   * It used to offer neither: `SetPublicationState` consumes an `iam_approvals` row and the
   * grantor's step-up INSIDE the transition's transaction, so a take-down from this card could only
   * ever end in `auth.four_eyes_required` — an advertised control with no path to a successful
   * outcome. The aggregate is the PARTICIPATION at its current version, which is the same row the
   * transition locks and CASes; there is no proposal, because the act is a verb applied to a
   * version.
   */
  const takedownHandoff: HandoffRequest | null =
    version === null
      ? null
      : {
          action: TAKE_DOWN,
          aggregateType: 'participation',
          aggregateId: participationId,
          expectedVersion: version,
          reasonCode: reason,
          label: `take down booth ${participationId}`,
        };
  const takedownStepUp = session.grantFor(TAKE_DOWN);

  /**
   * ONE INTENT PER VERB.
   *
   * The refusal surface below used to be wired to `runAction('take-down')` whatever had failed, so
   * a refused `suspend` offered a button that took the booth down instead — and a take-down whose
   * typed confirmation had been given once could be re-fired after the field was cleared. The
   * intent now carries its own verb, its own version and its own confirmation, and the remedy
   * re-sends THAT intent or nothing.
   *
   * `suspend` carries a typed confirmation of its own: it drops a live booth out of the world for
   * every visitor standing in front of it, and the id is typed before the intent is minted.
   */
  const runAction = (verb: string): void => {
    if (version === null) return;
    if (verb === 'take-down' && !grant) return;
    const body: Record<string, unknown> = { reason_code: reason, note };
    if (verb === 'take-down') body['legal_reference'] = legalRef;
    action.mutate(
      newIntent({
        path: `${resourcePath}:${verb}`,
        body,
        ifMatch: version,
        label: `${verb} ${participationId}`,
        ...(verb === 'take-down'
          ? { action: TAKE_DOWN, confirm: { expected: participationId, typed } }
          : {}),
        ...(verb === 'suspend' ? { confirm: { expected: participationId, typed: suspendTyped } } : {}),
      }),
    );
  };

  return (
    <>
      <Card title={`${t('admin.expo.participations')} · ${participationId.slice(0, 8)}`}>
        {participation.isLoading ? <LoadingState /> : null}
        {participation.error ? <ErrorState error={participation.error} /> : null}
        {participation.data ? (
          <dl className="kv">
            <dt>participation_id</dt>
            <dd>
              <Ltr>{participation.data.data.participation_id}</Ltr>
            </dd>
            <dt>{t('table.status')}</dt>
            <dd>{participation.data.data.status}</dd>
            <dt>publication_state</dt>
            <dd>{participation.data.data.publication_state}</dd>
            <dt>stage</dt>
            <dd>{participation.data.data.stage}</dd>
            <dt>{t('admin.participations.slot')}</dt>
            <dd>{participation.data.data.booth_slot_code ? <Ltr>{participation.data.data.booth_slot_code}</Ltr> : t('table.none')}</dd>
            <dt>{t('admin.participations.design')}</dt>
            <dd>{participation.data.data.booth_template_code ? <Ltr>{participation.data.data.booth_template_code}</Ltr> : t('table.none')}</dd>
            <dt>branding_approved_at</dt>
            <dd>{formatDateTime(participation.data.data.branding_approved_at)}</dd>
            <dt>{t('admin.expos.version')}</dt>
            <dd className="numeral">{version}</dd>
          </dl>
        ) : null}

        {participation.data ? (
          <form className="participation-design-form" onSubmit={(event) => {
            event.preventDefault();
            if (version === null) return;
            const current = participation.data?.data;
            updateDesign.mutate(newIntent({ path: resourcePath, method: 'PATCH', ifMatch: version,
              body: { booth_template_id: designId || current?.booth_template_id,
                commercial_tier: commercialTier || current?.commercial_tier } }));
          }}>
            <Select label={t('admin.participations.design')} name="assigned_booth_design"
              value={designId || participation.data.data.booth_template_id || ''} onChange={setDesignId}
              options={(designs.data?.items ?? []).map((design) => ({ value: design.booth_template_id,
                label: `${design.display_name} · ${design.width_m}×${design.depth_m}m` }))} required />
            <Select label={t('admin.participations.tier')} name="assigned_commercial_tier"
              value={commercialTier || participation.data.data.commercial_tier} onChange={setCommercialTier}
              options={['standard', 'premium', 'signature', 'flagship'].map((value) => ({ value,
                label: t(`admin.tier.${value}` as StringKey) }))} />
            <button className="btn btn--secondary" type="submit" disabled={updateDesign.isPending || version === null}>{t('admin.participations.saveDesign')}</button>
            {updateDesign.error ? <ErrorState error={updateDesign.error} /> : null}
          </form>
        ) : null}

        <Select
          label={t('guard.reason')}
          name="participation-reason"
          value={reason}
          options={REASON_CODES.map((c) => ({ value: c, label: c }))}
          onChange={setReason}
        />
        <TextArea label={t('guard.note')} name="participation-note" value={note} onChange={setNote} />

        <div className="row">
          {PUBLICATION_VERBS.map((verb) => (
            <button
              key={verb}
              type="button"
              className="btn btn--secondary"
              disabled={verbDisabled(verb)}
              onClick={() => runAction(verb)}
            >
              {t(VERB_KEYS[verb] ?? 'admin.verb.publish')}
            </button>
          ))}
        </div>
        {readinessData && !readinessData.publishable ? (
          <p className="muted">{t('admin.participations.publishBlocked')}</p>
        ) : null}
        <Field
          label={t('admin.participations.suspendConfirm')}
          hint={t('admin.participations.suspendHint')}
          name="suspend-typed"
          value={suspendTyped}
          onChange={setSuspendTyped}
          ltr
          {...(suspendTyped && suspendTyped !== participationId ? { error: t('guard.typed.mismatch') } : {})}
        />

        <div className="guard">
          <span className="guard__label">{t('admin.takedown.title')}</span>
          <p>{t('admin.takedown.desc')}</p>
          <Field label="legal_reference" name="legal_reference" value={legalRef} onChange={setLegalRef} ltr required />
          <Field
            label={t('guard.typed.label', { value: participationId })}
            name="takedown-typed"
            value={typed}
            onChange={setTyped}
            ltr
            {...(typed && typed !== participationId ? { error: t('guard.typed.mismatch') } : {})}
          />
          {takedownStepUp ? null : <StepUpPanel action={TAKE_DOWN} />}
          <ApprovalHandoff request={takedownHandoff} onGranted={setGrant} />
          <button
            type="button"
            className="btn btn--destructive"
            disabled={action.isPending || typed !== participationId || legalRef.trim() === '' || !grant}
            onClick={() => runAction('take-down')}
          >
            {t('admin.verb.take_down')}
          </button>
        </div>

        {action.error ? (
          <RefusalWithRemedy
            error={action.error}
            action={action.lastIntent.current?.action ?? TAKE_DOWN}
            retryExact={action.retryLast}
            onReread={() => {
              // The approval is bound to the version that just lost the CAS, so it is dead too.
              setGrant(null);
              void participation.refetch();
              void readiness.refetch();
            }}
          />
        ) : null}
      </Card>

      <Card title={t('readiness.title')} description={t('readiness.desc')} testId="participation-readiness">
        {readiness.isLoading ? <LoadingState /> : null}
        {readiness.error ? <ErrorState error={readiness.error} onRetry={() => void readiness.refetch()} /> : null}
        {readinessData ? <ReadinessPanel readiness={readinessData} /> : null}
      </Card>

      <Card title={t('admin.branding.title')} description={t('admin.branding.desc')}>
        <AuditStatement>
          <p>
            <Ltr>branding.approve</Ltr> — approval advances the construction stage to{' '}
            <Ltr>BRANDED</Ltr>.
          </p>
        </AuditStatement>
        {branding.isLoading ? <LoadingState /> : null}
        {branding.error ? <ErrorState error={branding.error} /> : null}
        {branding.data && (branding.data.items ?? []).length === 0 ? <EmptyState /> : null}
        {branding.data ? (
          <ul className="branding-review">
            {(branding.data.items ?? []).map((a) => {
              const rejectReason = brandingReasons[a.asset_id] ?? REASON_CODES[0] ?? 'operational';
              return (
                <li className="branding-review__item" key={a.asset_id}>
                  {/* THE REVIEWER LOOKS AT THE ASSET. The bytes are in quarantine until the scan
                      clears them, so the preview route — not a public URL — is what an approval is
                      actually based on. */}
                  <MediaPreview
                    src={adminMediaPreviewUrl(a.media_asset_id)}
                    alt={t('admin.branding.previewAlt', { kind: a.kind })}
                  />
                  <div className="branding-review__body">
                    <p className="row">
                      <Badge tone="neutral"><Ltr>{a.kind}</Ltr></Badge>
                      <Badge tone="neutral"><Ltr>{a.locale}</Ltr></Badge>
                      <Badge tone={statusTone(a.status)}>{a.status}</Badge>
                    </p>
                    <p className="muted">
                      <Ltr>{a.media_asset_id}</Ltr>
                    </p>
                    {a.reason_code ? (
                      <p className="muted">
                        {t('readiness.reason')}: <Ltr>{a.reason_code}</Ltr>
                      </p>
                    ) : null}
                    <Select
                      label={t('admin.branding.rejectReason')}
                      name={`branding-reason-${a.asset_id}`}
                      value={rejectReason}
                      options={REASON_CODES.map((c) => ({ value: c, label: c }))}
                      onChange={(next) => setBrandingReasons((current) => ({ ...current, [a.asset_id]: next }))}
                    />
                    <p className="row">
                      <button
                        type="button"
                        className="btn btn--small"
                        disabled={review.isPending}
                        onClick={() =>
                          review.mutate(newIntent({
                            path: `${resourcePath}/branding/${a.asset_id}:approve`,
                            body: { reason_code: rejectReason, note },
                            ifMatch: a.version,
                            label: `approve branding ${a.asset_id}`,
                          }))
                        }
                      >
                        {t('admin.branding.approve')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--destructive btn--small"
                        disabled={review.isPending}
                        onClick={() =>
                          review.mutate(newIntent({
                            path: `${resourcePath}/branding/${a.asset_id}:reject`,
                            body: { reason_code: rejectReason, note },
                            ifMatch: a.version,
                            label: `reject branding ${a.asset_id}`,
                          }))
                        }
                      >
                        {t('admin.branding.reject')}
                      </button>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {review.error ? <ErrorState error={review.error} /> : null}
      </Card>

      <Card title={t('admin.expo.revenue')}>
        <p className="muted">{t('admin.participations.desc')}</p>
        {revenue.error ? <ErrorState error={revenue.error} onRetry={() => void revenue.refetch()} /> : null}
        {revenue.data ? (
          <DataTable<RevenueItem>
            caption={t('admin.expo.revenue')}
            rows={revenue.data.items ?? []}
            rowKey={(r) => r.revenue_item_id}
            empty={<EmptyState />}
            columns={[
              { key: 'kind', header: t('table.kind'), cell: (r) => <Ltr>{r.kind}</Ltr> },
              { key: 'qty', header: 'quantity', cell: (r) => <span className="numeral">{r.quantity}</span> },
              {
                key: 'price',
                header: 'reference_price_minor',
                cell: (r) =>
                  r.reference_price_minor === undefined ? (
                    t('table.none')
                  ) : (
                    <span className="numeral">
                      {r.reference_price_minor} <Ltr>{r.currency ?? ''}</Ltr>
                    </span>
                  ),
              },
              { key: 'note', header: t('guard.note'), cell: (r) => r.note },
            ]}
          />
        ) : null}
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            addRevenue.mutate(newIntent({
              path: `${resourcePath}/revenue-items`,
              body: { kind, quantity: Number(quantity), note },
            }));
          }}
        >
          <Field label="kind" name="revenue-kind" value={kind} onChange={setKind} ltr />
          <Field label="quantity" name="revenue-qty" type="number" value={quantity} onChange={setQuantity} ltr />
          {addRevenue.error ? <ErrorState error={addRevenue.error} /> : null}
          <button className="btn" type="submit" disabled={addRevenue.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>
    </>
  );
}

/**
 * `ADM-05` — the hall layout register, READ-ONLY.
 *
 * There is nothing to choose here any more. The four legacy hall designs are gone, layouts are
 * generated by the solver from the expo's own participations and published from the floor console,
 * and the routes that created or published one by hand no longer exist. What remains is the record:
 * which scene, which version, what state, published when.
 */
export function AdminLayouts(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const statusLabel = (status: string): string => {
    const key = CATALOG_STATUS_KEYS[status];
    return key ? t(key) : status;
  };
  const layouts = useApiList<HallLayout>('/v1/admin/hall-layouts');

  return (
    <>
      <PageTitle title={t('admin.layouts.title')}>{t('admin.layouts.desc')}</PageTitle>
      <Card title={t('admin.layouts.title')} description={t('admin.layouts.readOnly')}>
        {layouts.isLoading ? <LoadingState /> : null}
        {layouts.error ? <ErrorState error={layouts.error} onRetry={() => void layouts.refetch()} /> : null}
        {layouts.data ? (
          <DataTable<HallLayout>
            caption={t('admin.layouts.title')}
            rows={layouts.data.items ?? []}
            rowKey={(l) => l.hall_layout_id}
            empty={<EmptyState>{t('admin.layouts.desc')}</EmptyState>}
            columns={[
              { key: 'code', header: t('table.code'), cell: (l) => <Ltr>{l.code}</Ltr> },
              { key: 'scene', header: t('admin.currentHall.sceneKey'), cell: (l) => <Ltr>{l.unity_scene_ref}</Ltr> },
              { key: 'version', header: t('admin.expos.version'), cell: (l) => <span className="numeral">{l.version}</span> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (l) => <Badge tone={statusTone(l.status)}>{statusLabel(l.status)}</Badge>,
              },
              { key: 'published', header: t('admin.layouts.publishedAt'), cell: (l) => formatDateTime(l.published_at) },
            ]}
          />
        ) : null}
      </Card>
    </>
  );
}
